import { AnchorLinkButton } from "@ui/components/link-button";
import { DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * A WhatsApp voice note (Ogg Opus) with the browser's own player, which the keyboard can drive,
 * and its transcript as the captions track. A browser that cannot play Ogg Opus gets the file
 * to download instead of a silent player.
 */
export function VoiceNote({
	src,
	transcript,
}: {
	src: string;
	transcript?: string | null;
}) {
	// Unknown until the page is live: the server cannot ask the browser.
	const [playable, setPlayable] = useState<boolean | null>(null);
	const [captions, setCaptions] = useState<string | undefined>(undefined);
	useEffect(() => {
		setPlayable(
			document
				.createElement("audio")
				.canPlayType('audio/ogg; codecs="opus"') !== "",
		);
	}, []);
	// A blob URL, which the admin's media-src allows (a data: URL is refused).
	useEffect(() => {
		const url = URL.createObjectURL(
			new Blob([captionsOf(transcript)], { type: "text/vtt" }),
		);
		setCaptions(url);
		return () => URL.revokeObjectURL(url);
	}, [transcript]);

	if (playable === false) {
		return (
			<AnchorLinkButton
				href={src}
				download
				variant="outline"
				size="sm"
				className="w-fit"
			>
				<DownloadIcon data-icon="inline-start" />
				Download the voice note
			</AnchorLinkButton>
		);
	}
	return (
		<audio
			controls
			preload="none"
			src={src}
			className="h-9 w-full min-w-56 max-w-xs"
			aria-label="Voice note"
		>
			<track kind="captions" label="Transcript" src={captions} default />
		</audio>
	);
}

/** The transcript as one WebVTT cue over the whole note, so captions say what was said. */
function captionsOf(transcript: string | null | undefined): string {
	const text =
		(transcript ?? "").replace(/-->/g, "->").trim() || "Voice note";
	return `WEBVTT\n\n00:00:00.000 --> 10:00:00.000\n${text}\n`;
}
